/**
 * /efficiency — chat-agent efficiency + correctness.
 *
 * Sourced from `GET /api/efficiency`:
 *   - "Recent requests" — a LIVE per-request feed read straight from
 *     `llm_call_traces`, so a request shows within ~a minute of being handled
 *     (mechanical metrics immediately; correctness/efficiency show "pending"
 *     until the nightly `chat_efficiency_daily` judge scores them). Auto-
 *     refreshes every 30s.
 *   - "Daily trend" — the judged per-day rollup the cron writes overnight.
 * Read-only, staff-facing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  type ChatEfficiencyRequest,
  type ChatEfficiencyResponse,
} from "../lib/api";
import { ConfigSuggestions } from "./ConfigSuggestions";
import { KnowledgeBaseSuggestions } from "./KnowledgeBaseSuggestions";
import { studioTimeZone } from "../lib/studio-tz";

const REFRESH_MS = 30_000;

/** The six judged axes, with the short label shown in the per-axis columns. */
const AXES: ReadonlyArray<{
  readonly key: keyof ChatEfficiencyRequest;
  readonly label: string;
}> = [
  { key: "correctness_score", label: "Correctness" },
  { key: "efficiency_score", label: "Efficiency" },
  { key: "truthfulness_score", label: "Truthful" },
  { key: "tone_score", label: "Tone" },
  { key: "goal_completion_score", label: "Goal" },
  { key: "escalation_score", label: "Escalation" },
  { key: "memory_capture_score", label: "Memory" },
];

/**
 * The lowest-scoring axis on a judged request — surfaced as a chip so the worst
 * dimension is visible at a glance. null when nothing is below 4 (handled well)
 * or no axis was scored.
 */
function worstAxis(
  r: ChatEfficiencyRequest,
): { readonly label: string; readonly score: number } | null {
  let worst: { label: string; score: number } | null = null;
  for (const a of AXES) {
    const v = r[a.key];
    if (typeof v !== "number") continue;
    if (worst === null || v < worst.score) worst = { label: a.label, score: v };
  }
  return worst !== null && worst.score < 4 ? worst : null;
}

function fmtCost(n: number): string {
  return `$${n.toFixed(n < 1 ? 4 : 2)}`;
}
function fmtSecs(ms: number | null): string {
  return ms === null ? "—" : `${(ms / 1000).toFixed(1)}s`;
}
function fmtScore(n: number | null): string {
  return n === null ? "—" : `${n}/5`;
}
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-GB", { timeZone: studioTimeZone() });
}

/** Colour a 1-5 score: ≥4 good, 3 ok, ≤2 poor. */
function scoreClass(n: number | null): string {
  if (n === null) return "";
  if (n >= 4) return "score-good";
  if (n >= 3) return "score-ok";
  return "score-poor";
}

export function Efficiency(): JSX.Element {
  const [data, setData] = useState<ChatEfficiencyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [judging, setJudging] = useState<string | null>(null);
  const [judgeError, setJudgeError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api.chatEfficiency();
      if (cancelledRef.current) return;
      setData(r);
      setError(null);
      setUpdatedAt(new Date());
    } catch (e) {
      if (cancelledRef.current) return;
      setError(
        e instanceof ApiError
          ? e.code ?? `http_${e.status}`
          : (e as Error).message,
      );
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void load();
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, [load]);

  // On-demand "Judge now" — judge one pending request + swap the row in place.
  const judgeNow = useCallback(async (sagaRunId: string): Promise<void> => {
    setJudging(sagaRunId);
    setJudgeError(null);
    try {
      const judged = await api.judgeChatRequest(sagaRunId);
      setData((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              recent: prev.recent.map((r) =>
                r.saga_run_id === sagaRunId ? judged : r,
              ),
            },
      );
    } catch (e) {
      setJudgeError(
        e instanceof ApiError ? e.code ?? `http_${e.status}` : (e as Error).message,
      );
    } finally {
      setJudging(null);
    }
  }, []);

  if (loading && data === null)
    return <p className="empty">Loading efficiency…</p>;
  if (error !== null && data === null) {
    return <p className="empty">Couldn&apos;t load efficiency ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  const hasData = data.daily.length > 0 || data.recent.length > 0;

  return (
    <div>
      <h1 className="page-title">Chat efficiency</h1>
      <p className="member-note">
        How efficiently each chat request was handled — tool calls, cost, speed,
        and an LLM-judged correctness. The recent feed is live from
        <code> llm_call_traces </code>; correctness/efficiency are scored
        overnight by the <code> chat_efficiency_daily </code> cron.
        {updatedAt !== null ? (
          <>
            {" · "}
            <span className="subtle">
              updated{" "}
              {updatedAt.toLocaleTimeString("en-GB", {
                timeZone: studioTimeZone(),
              })}{" "}
              studio time (auto every 30s)
            </span>
          </>
        ) : null}
      </p>

      <ConfigSuggestions />

      <KnowledgeBaseSuggestions />

      {!hasData ? (
        <p className="empty">
          No chat requests yet — this populates as soon as the agent handles a
          message.
        </p>
      ) : (
        <>
          <section className="panel">
            <h2>Recent requests (live)</h2>
            <p className="member-note">
              Newest first, straight from the raw traces. Correctness/efficiency
              show <em>pending</em> until judged — hit <strong>Judge now</strong>{" "}
              to score a request immediately instead of waiting for the nightly
              run.
            </p>
            {judgeError !== null ? (
              <p
                className="tag tag-danger"
                style={{ display: "inline-block", marginBottom: 8 }}
              >
                Judge failed: {judgeError}
              </p>
            ) : null}
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Turns</th>
                  <th>Tools</th>
                  <th>Cost</th>
                  <th>Latency</th>
                  <th>Model</th>
                  <th>Correct</th>
                  <th>Effic.</th>
                  <th>Truthful</th>
                  <th>Tone</th>
                  <th>Goal</th>
                  <th>Escal.</th>
                  <th>Memory</th>
                  <th>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.saga_run_id}>
                    <td>{fmtWhen(r.occurred_at)}</td>
                    <td>{r.turn_count}</td>
                    <td>
                      {r.tool_call_count}
                      {r.tool_names.length > 0 ? (
                        <span className="member-note">
                          {" "}
                          ({r.tool_names.join(", ")})
                        </span>
                      ) : null}
                    </td>
                    <td>{fmtCost(r.total_cost_usd)}</td>
                    <td>{fmtSecs(r.total_latency_ms)}</td>
                    <td className="subtle">{r.model}</td>
                    <td className={scoreClass(r.correctness_score)}>
                      {r.judged ? (
                        fmtScore(r.correctness_score)
                      ) : (
                        <span className="subtle">pending</span>
                      )}
                    </td>
                    <td className={scoreClass(r.efficiency_score)}>
                      {r.judged ? (
                        fmtScore(r.efficiency_score)
                      ) : (
                        <span className="subtle">pending</span>
                      )}
                    </td>
                    <td className={scoreClass(r.truthfulness_score)}>
                      {r.judged ? (
                        fmtScore(r.truthfulness_score)
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td className={scoreClass(r.tone_score)}>
                      {r.judged ? (
                        fmtScore(r.tone_score)
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td className={scoreClass(r.goal_completion_score)}>
                      {r.judged ? (
                        fmtScore(r.goal_completion_score)
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td className={scoreClass(r.escalation_score)}>
                      {r.judged ? (
                        fmtScore(r.escalation_score)
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td className={scoreClass(r.memory_capture_score)}>
                      {r.judged ? (
                        fmtScore(r.memory_capture_score)
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td>
                      {r.judged ? (
                        <>
                          {r.had_error ? "⚠ " : ""}
                          {r.failure_tag !== null ? (
                            <span
                              className="tag tag-danger"
                              style={{ marginRight: 6 }}
                            >
                              {r.failure_tag}
                            </span>
                          ) : null}
                          {(() => {
                            const w = worstAxis(r);
                            return w !== null ? (
                              <span
                                className="tag tag-danger"
                                style={{ marginRight: 6 }}
                                title="Lowest-scoring axis for this request"
                              >
                                {w.label} {w.score}/5
                              </span>
                            ) : null;
                          })()}
                          {r.judge_summary ?? "—"}
                          {r.improvement !== null ? (
                            <div
                              className="member-note"
                              style={{ marginTop: 2 }}
                            >
                              → {r.improvement}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={judging !== null}
                          onClick={() => void judgeNow(r.saga_run_id)}
                          title="Judge this request now instead of waiting for the nightly run"
                          style={{
                            background: "none",
                            border: "1px solid #2563eb",
                            color: "#1B33C0",
                            borderRadius: 4,
                            padding: "2px 8px",
                            cursor: judging !== null ? "wait" : "pointer",
                            font: "inherit",
                          }}
                        >
                          {judging === r.saga_run_id ? "Judging…" : "Judge now"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="panel">
            <h2>Daily trend</h2>
            <p className="member-note">
              Judged per-day rollup written by the nightly cron. Filtered to the
              current judge rubric (
              <code>{data.trend_judge_prompt_version}</code>) so a rubric change
              is never read as an agent trend.
              {data.trend_excluded_rows > 0 ? (
                <>
                  {" "}
                  <span className="subtle">
                    {data.trend_excluded_rows} scored row
                    {data.trend_excluded_rows === 1 ? "" : "s"} on an older
                    rubric excluded.
                  </span>
                </>
              ) : null}
            </p>
            {data.daily.length === 0 ? (
              <p className="empty">
                No judged days yet — the first lands the morning after a day of
                chat traffic.
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Requests</th>
                    <th>Avg turns</th>
                    <th>Avg tools</th>
                    <th>Total cost</th>
                    <th>Avg latency</th>
                    <th>Errors</th>
                    <th>Correctness</th>
                    <th>Efficiency</th>
                    <th>Truthful</th>
                    <th>Tone</th>
                    <th>Goal</th>
                    <th>Escalation</th>
                    <th>Memory</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((d) => (
                    <tr key={d.date}>
                      <td>{d.date}</td>
                      <td>{d.request_count}</td>
                      <td>{d.avg_turns ?? "—"}</td>
                      <td>{d.avg_tool_calls ?? "—"}</td>
                      <td>{fmtCost(d.total_cost_usd)}</td>
                      <td>{fmtSecs(d.avg_latency_ms)}</td>
                      <td>{d.error_count}</td>
                      <td className={scoreClass(d.avg_correctness)}>
                        {fmtScore(d.avg_correctness)}
                      </td>
                      <td className={scoreClass(d.avg_efficiency)}>
                        {fmtScore(d.avg_efficiency)}
                      </td>
                      <td className={scoreClass(d.avg_truthfulness)}>
                        {fmtScore(d.avg_truthfulness)}
                      </td>
                      <td className={scoreClass(d.avg_tone)}>
                        {fmtScore(d.avg_tone)}
                      </td>
                      <td className={scoreClass(d.avg_goal_completion)}>
                        {fmtScore(d.avg_goal_completion)}
                      </td>
                      <td className={scoreClass(d.avg_escalation)}>
                        {fmtScore(d.avg_escalation)}
                      </td>
                      <td className={scoreClass(d.avg_memory_capture)}>
                        {fmtScore(d.avg_memory_capture)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
